import { managedModelIds } from "./aiProviders";

export type ModelPricing = {
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion?: number;
  cacheWritePerMillion?: number;
};

export type ModelPricingRow = {
  aiProviderId: string;
  modelId: string;
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion?: number | null;
  cacheWritePerMillion?: number | null;
};

export function buildModelPricingLookup(
  rows: readonly ModelPricingRow[]
): Map<string, ModelPricing> {
  const map = new Map<string, ModelPricing>();
  for (const row of rows) {
    map.set(pricingKey(row.aiProviderId, row.modelId), {
      inputPerMillion: row.inputPerMillion,
      outputPerMillion: row.outputPerMillion,
      cacheReadPerMillion: row.cacheReadPerMillion ?? undefined,
      cacheWritePerMillion: row.cacheWritePerMillion ?? undefined,
    });
  }
  return map;
}

function pricingKey(aiProviderId: string | null | undefined, model: string) {
  return aiProviderId ? `${aiProviderId}:${model}` : model;
}

// Bedrock meters usage under region-scoped inference-profile ids
// (`us.anthropic.claude-opus-4-8`); strip the geo/vendor prefix so pricing keyed
// by the bare model id still resolves. Returns the id unchanged otherwise.
export function canonicalPricingModelId(model: string): string {
  const match = /^(?:[a-z][a-z0-9-]*\.)?anthropic\.(.+)$/.exec(model);
  return match ? match[1]! : model;
}

type TokenUsage = {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
};

export function estimateUsageCostUsd(
  model: string,
  usage: TokenUsage,
  pricing?: ReadonlyMap<string, ModelPricing>,
  aiProviderId?: string | null
): number | null {
  const modelPricing =
    pricing?.get(pricingKey(aiProviderId, model)) ??
    pricing?.get(pricingKey(aiProviderId, canonicalPricingModelId(model)));
  if (!modelPricing) return null;

  // inputTokens is the grand total input, which already includes the cache-read
  // and cache-write tokens; those bill at their own rates, so only the fresh
  // remainder is priced at the full input rate or they'd be charged twice.
  const freshInputTokens = Math.max(
    0,
    usage.inputTokens -
      (usage.cacheReadTokens ?? 0) -
      (usage.cacheWriteTokens ?? 0)
  );
  const inputCost =
    (freshInputTokens / 1_000_000) * modelPricing.inputPerMillion;
  const outputCost =
    (usage.outputTokens / 1_000_000) * modelPricing.outputPerMillion;
  const cacheReadCost =
    modelPricing.cacheReadPerMillion != null
      ? ((usage.cacheReadTokens ?? 0) / 1_000_000) *
        modelPricing.cacheReadPerMillion
      : 0;
  const cacheWriteCost =
    modelPricing.cacheWritePerMillion != null
      ? ((usage.cacheWriteTokens ?? 0) / 1_000_000) *
        modelPricing.cacheWritePerMillion
      : 0;

  return inputCost + outputCost + cacheReadCost + cacheWriteCost;
}

// List prices (USD per 1M tokens) for well-known models, keyed by the raw model
// id the provider returns (the dimension usage meters), used to seed connector
// pricing defaults; a user-entered rate always overrides. Model ids:
// platform.claude.com/docs/en/about-claude/models/overview and
// developers.openai.com/api/docs/models/all
export const DEFAULT_MODEL_PRICING: Record<string, ModelPricing> = {
  "claude-fable-5": {
    inputPerMillion: 10,
    outputPerMillion: 50,
    cacheReadPerMillion: 1,
    cacheWritePerMillion: 12.5,
  },
  "claude-sonnet-5": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-opus-4-8": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-7": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-6": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-6-v1": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-sonnet-4-6": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-opus-4-5": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-5-20251101": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-5-20251101-v1:0": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-haiku-4-5": {
    inputPerMillion: 1,
    outputPerMillion: 5,
    cacheReadPerMillion: 0.1,
    cacheWritePerMillion: 1.25,
  },
  "claude-haiku-4-5-20251001": {
    inputPerMillion: 1,
    outputPerMillion: 5,
    cacheReadPerMillion: 0.1,
    cacheWritePerMillion: 1.25,
  },
  "claude-haiku-4-5-20251001-v1:0": {
    inputPerMillion: 1,
    outputPerMillion: 5,
    cacheReadPerMillion: 0.1,
    cacheWritePerMillion: 1.25,
  },
  "claude-sonnet-4-5": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-sonnet-4-5-20250929": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-sonnet-4-5-20250929-v1:0": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "gpt-5.6-sol": {
    inputPerMillion: 5,
    outputPerMillion: 30,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "gpt-5.6-terra": {
    inputPerMillion: 2.5,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.25,
    cacheWritePerMillion: 3.125,
  },
  "gpt-5.6-luna": {
    inputPerMillion: 1,
    outputPerMillion: 6,
    cacheReadPerMillion: 0.1,
    cacheWritePerMillion: 1.25,
  },
  "gpt-5.5-pro": { inputPerMillion: 30, outputPerMillion: 180 },
  "gpt-5.5": {
    inputPerMillion: 5,
    outputPerMillion: 30,
    cacheReadPerMillion: 0.5,
  },
  "gpt-5.4-pro": { inputPerMillion: 30, outputPerMillion: 180 },
  "gpt-5.4": {
    inputPerMillion: 2.5,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.25,
  },
  "gpt-5.4-mini": {
    inputPerMillion: 0.75,
    outputPerMillion: 4.5,
    cacheReadPerMillion: 0.075,
  },
  "gpt-5.4-nano": {
    inputPerMillion: 0.2,
    outputPerMillion: 1.25,
    cacheReadPerMillion: 0.02,
  },
  "gpt-5.3-codex": {
    inputPerMillion: 1.75,
    outputPerMillion: 14,
    cacheReadPerMillion: 0.175,
  },
  "gpt-5.2": {
    inputPerMillion: 1.75,
    outputPerMillion: 14,
    cacheReadPerMillion: 0.175,
  },
  "gpt-5.1": {
    inputPerMillion: 1.25,
    outputPerMillion: 10,
    cacheReadPerMillion: 0.125,
  },
  "gpt-5": {
    inputPerMillion: 1.25,
    outputPerMillion: 10,
    cacheReadPerMillion: 0.125,
  },
  "gpt-5-mini": {
    inputPerMillion: 0.25,
    outputPerMillion: 2,
    cacheReadPerMillion: 0.025,
  },
  "gpt-5-nano": {
    inputPerMillion: 0.05,
    outputPerMillion: 0.4,
    cacheReadPerMillion: 0.005,
  },
};

// Managed usage has no aiProviderId, so pricing resolves through bare model ids.
export const MANAGED_MODEL_PRICING_ESTIMATES: Record<string, ModelPricing> =
  Object.fromEntries(
    managedModelIds.map((model) => [model, DEFAULT_MODEL_PRICING[model]!])
  );
